<script setup lang="ts">
import {ref} from "vue";
import IconChevronBottom from "@/assets/icons/IconChevronBottom.vue";
import UploadFileItem from "@/components/modules/FileUploadModal/UploadFileItem.vue";
import ProgressBar from "@/components/modules/FileUploadModal/ProgressBar.vue";
import IconCheck from "@/assets/icons/IconCheck.vue";
import {FileStatus} from "@/types/files";

interface FileData {
  file_id: string;
  file_name: string;
  file_size: number;
  error: null | string;
  status?: FileStatus;
  progress?: number;
  isUploading?: boolean;
}

interface Props {
  files: FileData[];
  totalSize?: string;
  statusText?: string;
  progress?: number;
  isUploading?: boolean;
  isError?: boolean;
  isFileUploadingComplete?: boolean;
}

withDefaults(defineProps<Props>(), {
  totalSize: "",
  statusText: "Загрузка завершена",
  progress: 0,
  isUploading: false,
  isFileUploadingComplete: false
});

const emit = defineEmits<{
  retryFile: [fileId: string];
  deleteFile: [fileId: string];
  cancelFile: [fileId: string];
}>();

const isExpanded = ref(false);

const toggleExpanded = () => {
  isExpanded.value = !isExpanded.value;
};
</script>

<template>
  <div
    class="upload-card"
    :class="{'upload-card--expanded': isExpanded}"
  >
    <template v-if="files.length > 1">
      <div class="upload-card__header">
        <div class="upload-card__title">
          <span class="upload-card__title-text">{{ files.length }} файлов</span>
          <template v-if="!isUploading && progress === 100">
            <span class="upload-card__title-text"> · </span>
            <span
              :class="[isError ? 'upload-card__title-text--error' : 'upload-card__title-text--status']"
              class="upload-card__title-text"
            >
              {{ statusText }}
            </span>
          </template>
        </div>
        <button
          class="upload-card__toggle"
          :class="{'upload-card__toggle--active': isExpanded}"
          @click="toggleExpanded"
        >
          <IconChevronBottom v-if="!isFileUploadingComplete" />
          <IconCheck v-if="isFileUploadingComplete" />
        </button>
      </div>

      <template v-if="!isExpanded && totalSize">
        <span class="upload-card__size">
          {{ totalSize }}
        </span>
        <ProgressBar
          v-if="isUploading || isFileUploadingComplete"
          :progress="progress"
          :is-uploading="isUploading"
        />
      </template>




      <div
        v-else
        class="upload-card__list"
      >
        <template
          v-for="(file, index) in files"
          :key="file.file_id"
        >
          <UploadFileItem
            :file-name="file.file_name"
            :file-size="file.file_size"
            :error="file.error"
            :status="file.status"
            :progress="file.progress !== undefined ? file.progress : progress"
            :is-file-uploading-complete="file.isUploading !== undefined ? !file.isUploading && file.progress === 100 : isFileUploadingComplete"
            @retry="emit('retryFile', file.file_id)"
            @delete="emit('deleteFile', file.file_id)"
            @cancel="emit('cancelFile', file.file_id)"
          />
          <div
            v-if="index !== files.length - 1"
            class="hr-line"
          />
        </template>
      </div>
    </template>

    <template v-else-if="files.length === 1">
      <UploadFileItem
        :status="files[0].status"
        :progress="files[0].progress !== undefined ? files[0].progress : progress"
        :error="files[0].error"
        :file-name="files[0].file_name"
        :file-size="files[0].file_size"
        :is-file-uploading-complete="files[0].isUploading !== undefined ? !files[0].isUploading && files[0].progress === 100 : isFileUploadingComplete"
        @retry="emit('retryFile', files[0].file_id)"
        @delete="emit('deleteFile', files[0].file_id)"
        @cancel="emit('cancelFile', files[0].file_id)"
      />
    </template>
  </div>
</template>

<style scoped lang="scss">
.hr-line {
  width: 100%;
  height: 2px;
  background-color: #F7F8FD;
}

.upload-card {
  border: 1px solid var(--border-bd-divider);
  padding: 14px 16px;
  border-radius: 10px;
  gap: 4px;
  display: flex;
  flex-direction: column;

  &--expanded {
    gap: 14px;
  }

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  &__title {
    display: flex;
    gap: 4px;
  }

  &__title-text {
    font-weight: 500;
    font-size: 15px;
    line-height: 18px;
    color: var(--black-black90);

    &--error {
      color: var(--system-red)
    }

    &--status {
      color: #36AE2B;
    }
  }

  &__size {
    font-weight: 400;
    font-size: 12px;
    line-height: 14px;
    color: var(--gray-gray90);
  }

  &__toggle {
    appearance: none;
    background-color: transparent;
    border: none;
    padding: 0;
    color: var(--icon-icon-secondary);
    width: 24px;
    height: 24px;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    user-select: none;
    transition: transform 0.3s ease;

    &--active {
      transform: rotate(-180deg);
    }

    svg {
      width: 14px;
      height: 14px;
    }
  }

  &__list {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
}
</style>